import { Lock, Database, Share2, UserCheck, Cookie, Mail } from "lucide-react";

export default function Privacy() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
            <div className="mb-10">
                <div className="overline mb-3">Legal</div>
                <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    Privacy Policy<span className="text-accent">.</span>
                </h1>
                <p className="text-zinc-500">Last updated: February 2026</p>
            </div>

            <div className="surface p-8 mb-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-accent/30 bg-accent/10 flex-shrink-0 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-accent" strokeWidth={1.75} />
                </div>
                <div>
                    <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-1">
                        Your product photos are yours
                    </h2>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                        We use them only to generate your listing copy. We do not sell them, we do not
                        train third-party models on them, and you can request deletion at any time.
                    </p>
                </div>
            </div>

            <div className="space-y-8 text-zinc-300 leading-relaxed">
                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Database className="w-4 h-4 text-accent" /> 1. What we collect
                    </h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li><span className="text-zinc-200">Account data:</span> name, email address, hashed password.</li>
                        <li><span className="text-zinc-200">Product content:</span> images you upload (JPEG/PNG/WEBP), titles, categories, keywords and any specs you enter.</li>
                        <li><span className="text-zinc-200">Generated output:</span> AI-generated titles, descriptions, bullet points, meta tags and variant copy stored against your account.</li>
                        <li><span className="text-zinc-200">Billing data:</span> Razorpay payment reference IDs, amount, currency, invoice numbers. We do NOT store your card, UPI or bank details on our servers — that stays with Razorpay.</li>
                        <li><span className="text-zinc-200">Technical data:</span> IP address, browser type, timestamps, basic logs needed to keep the service secure and running.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">2. How we use it</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>To generate listing copy from the photos and specs you upload.</li>
                        <li>To keep your account, credits and past listings available in your dashboard.</li>
                        <li>To process payments and issue invoices.</li>
                        <li>To send transactional emails (payment confirmations, password reset links).</li>
                        <li>To detect abuse, prevent fraud and keep the service secure.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">3. How your images and specs are handled</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>Uploaded images and specs are stored in our MongoDB database, associated with your account.</li>
                        <li>They are sent to our AI model provider (see next section) only to produce the listing you requested.</li>
                        <li>They are not shared with any advertiser, marketplace, or third party for training or marketing.</li>
                        <li>You can delete individual listings from the dashboard or ask us to delete your entire account.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-accent" /> 4. Third-party processors
                    </h3>
                    <p className="text-sm text-zinc-400 mb-2">
                        We use a small number of trusted vendors to run the service. Each receives only the data needed to do its job:
                    </p>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li><span className="text-zinc-200">Anthropic (via Emergent):</span> receives your uploaded image and specs to generate the listing text. Anthropic states it does not use API traffic to train its models by default.</li>
                        <li><span className="text-zinc-200">Razorpay:</span> processes payments. Receives your name, email, and payment amount. Payment method details (card / UPI / netbanking) are handled entirely by Razorpay.</li>
                        <li><span className="text-zinc-200">Hosting &amp; database:</span> our servers and MongoDB store account, listing and billing data.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Cookie className="w-4 h-4 text-accent" /> 5. Cookies
                    </h3>
                    <p className="text-sm text-zinc-400">
                        listcraft uses a single HTTP-only authentication cookie to keep you signed in.
                        We do not use tracking or advertising cookies. Clearing your browser cookies will
                        sign you out.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">6. Data retention</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>Account, listing and billing data are retained as long as your account is active.</li>
                        <li>Invoices are retained for at least 8 years to comply with Indian tax law.</li>
                        <li>Password reset tokens expire within a short time window and are single-use.</li>
                        <li>On account deletion, listings and images are removed within 7 business days. Billing records are retained per the point above.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-accent" /> 7. Your rights
                    </h3>
                    <p className="text-sm text-zinc-400 mb-2">You can, at any time, ask us to:</p>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>Access a copy of the personal data we hold about you.</li>
                        <li>Correct information that is wrong or out of date.</li>
                        <li>Delete your account and associated listings.</li>
                        <li>Restrict or object to specific processing.</li>
                    </ul>
                    <p className="text-sm text-zinc-400 mt-2">
                        Email <a href="mailto:arienterprises50@gmail.com" className="text-accent hover:underline">arienterprises50@gmail.com</a> from
                        your registered email address and we&apos;ll respond within 7 business days.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">8. Security</h3>
                    <p className="text-sm text-zinc-400">
                        Passwords are stored using bcrypt. Traffic between your browser and our servers
                        is encrypted via HTTPS. Payment secrets never touch our servers. No system is
                        perfectly secure, but we work to keep listcraft safe and to notify affected users
                        promptly in the unlikely event of a breach.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">9. Children</h3>
                    <p className="text-sm text-zinc-400">
                        listcraft is not intended for use by anyone under 18. We do not knowingly collect
                        data from minors. If you believe a minor has created an account, email us and we
                        will delete it.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">10. Changes to this policy</h3>
                    <p className="text-sm text-zinc-400">
                        We may update this Privacy Policy occasionally. Material changes will be
                        announced in-app or via email. Continued use of the service after an update
                        constitutes acceptance of the revised policy.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-accent" /> 11. Contact
                    </h3>
                    <p className="text-sm text-zinc-400">
                        Ari Enterprises · <a href="mailto:arienterprises50@gmail.com" className="text-accent hover:underline">arienterprises50@gmail.com</a>
                    </p>
                </section>
            </div>
        </div>
    );
}
